import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getMonthName, getCurrentYear, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, CreditCard, Upload, Loader2, Truck } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { ExportButtons } from '../components/ExportButtons';
import { MissingDatesWarning } from '../components/MissingDatesWarning';
import { SnapshotButtons } from '../components/SnapshotButtons';

export function PlateformesPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [plateformes, setPlateformes] = useState([]);
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterPlateforme, setFilterPlateforme] = useState('all');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPlateformes();
  }, [year, month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { annee: year };
      if (month) params.mois = month;
      const res = await api.get('/revenus-plateformes', { params });
      setData(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlateformes = async () => {
    try {
      const res = await api.get('/listes/plateforme');
      setPlateformes(res.data.map(m => m.valeur));
      if (res.data.length === 0) {
        setPlateformes(['Deliveroo', 'Uber Eats', 'Just Eat']);
      }
    } catch (error) {
      setPlateformes(['Deliveroo', 'Uber Eats', 'Just Eat']);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/revenus-plateformes/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.imported} ligne(s) importée(s) (${res.data.dates_count} jours)`);
      setImportDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/revenus-plateformes/${id}`);
      toast.success('Supprimé');
      selectedIds.delete(id);
      setSelectedIds(new Set(selectedIds));
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await api.post('/revenus-plateformes/delete-bulk', { ids: [...selectedIds] });
      toast.success(`${selectedIds.size} ligne(s) supprimée(s)`);
      setSelectedIds(new Set());
      fetchData();
    } catch (error) {
      toast.error('Erreur suppression');
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    const selectable = filteredData.filter(d => !d.auto);
    if (selectedIds.size === selectable.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(selectable.map(d => d.id)));
  };

  const uniquePlateformes = [...new Set(data.map(d => d.plateforme))].sort();

  const filteredData = data.filter(d => {
    if (dateFrom && d.date < dateFrom) return false;
    if (dateTo && d.date > dateTo) return false;
    if (filterPlateforme !== 'all' && d.plateforme !== filterPlateforme) return false;
    return true;
  });
  const totalBrut = filteredData.reduce((sum, r) => sum + r.revenu_brut, 0);
  const totalNet = filteredData.reduce((sum, r) => sum + r.revenu_net, 0);

  const PlateformeForm = ({ item, onClose, defaultDate }) => {
    const [form, setForm] = useState(item ? {
      date: item.date,
      plateforme: item.plateforme,
      revenu_brut: item.revenu_brut,
      commission: item.commission,
      tva_sur_commission: item.tva_sur_commission,
      frais_marketing: item.frais_marketing || 0,
      eco_contribution: item.eco_contribution || 0
    } : {
      date: defaultDate || toISODateString(new Date()),
      plateforme: 'Deliveroo',
      revenu_brut: 0,
      commission: 0.30,
      tva_sur_commission: 0.20,
      frais_marketing: 0,
      eco_contribution: 0
    });
    const [dateOpen, setDateOpen] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) {
          await api.put(`/revenus-plateformes/${item.id}`, form);
          toast.success('Modifié');
        } else {
          await api.post('/revenus-plateformes', form);
          toast.success('Créé');
        }
        fetchData();
        onClose();
      } catch (error) {
        toast.error('Erreur');
      }
    };

    const tvaComm = form.revenu_brut * form.commission * form.tva_sur_commission;
    const revenuNet = (form.revenu_brut - ((form.commission * form.revenu_brut) + tvaComm)) - form.frais_marketing - form.eco_contribution;

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date ? format(new Date(form.date), 'PPP', { locale: fr }) : 'Choisir'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={form.date ? new Date(form.date) : undefined}
                  onSelect={(date) => {
                    setForm({ ...form, date: toISODateString(date) });
                    setDateOpen(false);
                  }}
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Plateforme</Label>
            <Select value={form.plateforme} onValueChange={(v) => setForm({ ...form, plateforme: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {plateformes.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Revenu brut (EUR)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.revenu_brut}
              onChange={(e) => setForm({ ...form, revenu_brut: parseFloat(e.target.value) || 0 })}
            />
          </div>

          <div className="space-y-2">
            <Label>Commission (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.commission * 100}
              onChange={(e) => setForm({ ...form, commission: parseFloat(e.target.value) / 100 || 0 })}
            />
          </div>

          <div className="space-y-2">
            <Label>TVA sur commission (%)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.tva_sur_commission * 100}
              onChange={(e) => setForm({ ...form, tva_sur_commission: parseFloat(e.target.value) / 100 || 0 })}
            />
          </div>

          <div className="space-y-2">
            <Label>Frais marketing (EUR)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.frais_marketing}
              onChange={(e) => setForm({ ...form, frais_marketing: parseFloat(e.target.value) || 0 })}
            />
          </div>

          <div className="space-y-2">
            <Label>Eco-contribution (EUR)</Label>
            <Input
              type="number"
              step="0.01"
              value={form.eco_contribution}
              onChange={(e) => setForm({ ...form, eco_contribution: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
          <div>
            <span className="text-xs text-muted-foreground">TVA commission</span>
            <p className="text-lg font-medium tabular-nums">{formatCurrency(tvaComm)}</p>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Revenu Net</span>
            <p className="text-lg font-bold text-primary tabular-nums">{formatCurrency(revenuNet)}</p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline">Annuler</Button></DialogClose>
          <Button type="submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  const getPlatformBadgeClass = (plateforme) => {
    if (plateforme.toLowerCase().includes('deliveroo')) return 'bg-[#00CCBC] text-white';
    if (plateforme.toLowerCase().includes('uber')) return 'bg-[#06C167] text-white';
    return 'bg-secondary';
  };

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Revenus Plateformes</h1>
          <p className="text-muted-foreground mt-1">Deliveroo, Uber Eats et autres plateformes</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SnapshotButtons module="plateformes" moduleLabel="Plateformes" />
          <ExportButtons api={api} moduleKey="plateformes" year={year} month={month} />
          <Button variant="outline" className="gap-2" onClick={() => navigate('/uber-verification')} data-testid="uber-verif-btn">
            <Truck className="w-4 h-4" /> Vérification Uber
          </Button>
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="import-plateforme-btn">
                <Upload className="w-4 h-4" /> Importer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importer des revenus UBER</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Importez un fichier CSV ou XLSX avec les colonnes :<br/>
                  <strong>A:</strong> Date de la commande (JJ/MM/AAAA)<br/>
                  <strong>B:</strong> Montant moyen des commandes
                </p>
                <div className="p-3 rounded-md bg-muted text-sm space-y-1">
                  <p>Commission UBER : <strong>33%</strong></p>
                  <p>TVA sur commission : <strong>20%</strong></p>
                  <p>Frais marketing : <strong>5€/jour</strong></p>
                </div>
                <div>
                  <Label htmlFor="import-file">Fichier CSV ou XLSX</Label>
                  <Input
                    id="import-file"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleImportFile}
                    disabled={importing}
                    className="mt-1"
                    data-testid="import-plateforme-file"
                  />
                </div>
                {importing && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" /> Import en cours...
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="add-plateforme-btn" onClick={() => setEditItem(null)}>
                <Plus className="w-4 h-4" /> Nouveau revenu
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editItem ? 'Modifier' : 'Nouveau'} Revenu Plateforme</DialogTitle>
              </DialogHeader>
              <PlateformeForm item={editItem} onClose={() => { setDialogOpen(false); setPrefillDate(null); }} defaultDate={prefillDate} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
              <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Tous les mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterPlateforme} onValueChange={setFilterPlateforme}>
          <SelectTrigger className="w-[180px]" data-testid="filter-plateforme">
            <SelectValue placeholder="Plateforme" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les plateformes</SelectItem>
            {uniquePlateformes.map(p => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Du</Label>
          <Input type="date" className="w-[150px] h-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <Label className="text-xs whitespace-nowrap">Au</Label>
          <Input type="date" className="w-[150px] h-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          {(dateFrom || dateTo || filterPlateforme !== 'all') && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); setFilterPlateforme('all'); }}>Effacer</Button>}
        </div>

        <div className="ml-auto flex items-center gap-4">
          <span className="text-sm text-muted-foreground">{filteredData.length} résultat(s)</span>
          <div className="px-3 py-2 bg-muted rounded-full text-sm">
            Brut: <span className="font-medium tabular-nums">{formatCurrency(totalBrut)}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
            <CreditCard className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium">Net:</span>
            <span className="text-lg font-bold text-primary tabular-nums">{formatCurrency(totalNet)}</span>
          </div>
        </div>
      </div>

      {/* Bulk delete bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-testid="bulk-delete-bar">
          <span className="text-sm font-medium">{selectedIds.size} ligne(s) sélectionnée(s)</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="deselect-all-btn">
            Désélectionner
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" className="gap-1" data-testid="bulk-delete-btn">
                <Trash2 className="w-4 h-4" /> Supprimer la sélection
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedIds.size} revenu(s) plateforme ?</AlertDialogTitle>
                <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteSelected}>Supprimer</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Missing dates warning */}
      <MissingDatesWarning data={filteredData} dateField="date" year={year} month={month ? parseInt(month) : null} dateFrom={dateFrom} dateTo={dateTo} onDateClick={(date) => { setPrefillDate(date); setEditItem(null); setDialogOpen(true); }} />

      {/* Banner Uber auto */}
      {filteredData.some(d => d.auto) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm flex items-start gap-2" data-testid="banner-uber-auto">
          <Truck className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
          <div>
            <p className="font-semibold text-emerald-900">Uber Eats synchronisé automatiquement</p>
            <p className="text-emerald-700 text-xs">Les lignes <Badge variant="outline" className="text-[9px] h-4 px-1 bg-emerald-100 border-emerald-300 text-emerald-700 mx-0.5">AUTO</Badge> proviennent de vos imports Vérif. Uber. Modifiez-les depuis <button onClick={() => navigate('/uber-verification')} className="underline font-medium hover:text-emerald-900">l'onglet Vérif. Uber</button>.</p>
          </div>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                    checked={filteredData.length > 0 && selectedIds.size === filteredData.length}
                    onChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Plateforme</TableHead>
                <TableHead className="text-right">Brut</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-right">TVA Comm.</TableHead>
                <TableHead className="text-right">Frais</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    Aucun revenu plateforme pour cette période
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map(item => (
                  <TableRow key={item.id} className={`${selectedIds.has(item.id) ? 'bg-primary/5' : ''} ${item.auto ? 'bg-emerald-50/40' : ''}`}>
                    <TableCell>
                      {item.auto ? (
                        <span className="inline-block w-4 h-4" title="Ligne automatique non sélectionnable" />
                      ) : (
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                          checked={selectedIds.has(item.id)}
                          onChange={() => toggleSelect(item.id)}
                          data-testid={`select-row-${item.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(item.date)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <Badge className={getPlatformBadgeClass(item.plateforme)}>
                          {item.plateforme}
                        </Badge>
                        {item.auto && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 bg-emerald-100 border-emerald-300 text-emerald-700" title="Calculé automatiquement depuis Vérif. Uber">
                            AUTO
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(item.revenu_brut)}</TableCell>
                    <TableCell className="text-right tabular-nums">{(item.commission * 100).toFixed(0)}%</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(item.tva_collectee_sur_commission)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {formatCurrency((item.frais_marketing || 0) + (item.eco_contribution || 0))}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(item.revenu_net)}</TableCell>
                    <TableCell>
                      {item.auto ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs text-emerald-700"
                          onClick={() => navigate('/uber-verification')}
                          title="Modifier via Vérif. Uber"
                          data-testid="goto-uber-verif"
                        >
                          Vérif. Uber →
                        </Button>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setDialogOpen(true); }}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Supprimer ?</AlertDialogTitle>
                                <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(item.id)}>Supprimer</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {filteredData.length > 0 && (
              <tfoot>
                <TableRow className="bg-muted/50 font-bold">
                  <TableCell></TableCell>
                  <TableCell>Total ({filteredData.length} lignes)</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(totalBrut)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(filteredData.reduce((s, r) => s + (r.tva_collectee_sur_commission || 0), 0))}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(filteredData.reduce((s, r) => s + (r.frais_marketing || 0) + (r.eco_contribution || 0), 0))}</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">{formatCurrency(totalNet)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </tfoot>
            )}
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
